<template>
    <div>
        <Head :title="__(title)" />
        <div class="flex flex-col flex-1 main sec_chat">

            <div class="flex flex-1 h-full gap-0 lg:gap-5">
                <!-- Conversations Sidebar -->
                <div class="w-full h-full overflow-hidden bg-white sidebar lg:w-1/3" :class="showSidebar ? 'flex flex-col' : 'hidden lg:flex lg:flex-col'">
                    <div class="flex flex-col h-full chat-box">
                        <div class="flex flex-col h-full chat-left-aside">
                            <div class="c-top">
                                <div class="mb-1 media">
                                    <img v-if="user.photo_path" :src="user.photo_path" alt="" class="rounded-full rounded-circle user-image">
                                    <img v-else src="/images/svg/profile.svg" alt="" class="rounded-full rounded-circle user-image">
                                    <div class="about">
                                        <div class="name f-w-600">{{ user.first_name }} {{ user.last_name }}</div>
                                        <div class="status">{{ __(user.role.name) }}</div>
                                    </div>
                                </div>
                                
                                <!-- New Chat Button - Only for users with create permission -->
                                <div v-if="user_access.chat.create" class="flex gap-2 py-2">
                                    <button @click="showNewChatModal = true" class="flex-1 px-4 py-2 text-sm text-white bg-blue-500 rounded hover:bg-blue-600">
                                        <icon name="plus" class="inline-block w-4 h-4 mr-1" /> New Chat
                                    </button>
                                    <button @click="showNewGroupModal = true" class="flex-1 px-4 py-2 text-sm text-white bg-green-500 rounded hover:bg-green-600">
                                        <icon name="users" class="inline-block w-4 h-4 mr-1" /> New Group
                                    </button>
                                </div>

                                <div class="py-2 search flex-2">
                                    <search-input v-model="form.search" class="w-full" @reset="reset"></search-input>
                                </div>
                            </div>
                            
                            <div class="flex-1 overflow-hidden c-list">
                                <div class="h-full pt-2 pb-2 overflow-y-auto">
                                    <Link v-for="conversation in conversations.data" :key="conversation.id" 
                                         :href="route('chat.show', conversation.id)"
                                         @click="handleConversationClick"
                                         class="flex p-3 mx-3 mb-2 transition-all duration-200 border rounded-lg shadow-sm cursor-pointer entry hover:bg-gray-50"
                                         v-bind:class="{ 'bg-blue-100 border-blue-500 border-l-4 shadow-md': chat && (conversation.id === chat.id), 'border-gray-100': !chat || (conversation.id !== chat.id) }">
                                        
                                        <div class="flex-2">
                                            <div class="relative w-12 h-12">
                                                <img v-if="conversation.icon" :src="conversation.icon" class="object-cover w-12 h-12 rounded-full" />
                                                <span v-else class="flex items-center justify-center w-12 h-12 mx-auto text-lg font-semibold text-white rounded-full user_icon">
                                                    {{ getInitial(conversation.title) }}
                                                </span>
                                            </div>
                                        </div>
                                        
                                        <div class="flex-1 px-2 overflow-hidden">
                                            <div class="truncate"><span class="font-semibold">{{ conversation.title }}</span></div>
                                            <div class="mt-0.5" v-if="conversation.is_group">
                                                <small class="text-xs text-gray-400">{{ conversation.participants_count }} {{ conversation.participants_count === 1 ? 'member' : 'members' }}</small>
                                            </div>
                                            <div class="mt-0.5 truncate" v-if="conversation.last_message">
                                                <small class="text-xs text-gray-600">{{ conversation.last_message }}</small>
                                            </div>
                                        </div>
                                        
                                        <div class="flex flex-col items-end justify-between text-right flex-2">
                                            <div><small class="text-xs text-gray-400">{{ formatDate(conversation.last_message_at) }}</small></div>
                                            <div>
                                                <small v-if="conversation.unread_count" class="inline-block w-5 h-5 text-xs font-medium leading-5 text-center text-white bg-red-500 rounded-full">
                                                    {{ conversation.unread_count > 9 ? '9+' : conversation.unread_count }}
                                                </small>
                                            </div>
                                        </div>
                                    </Link>
                                    
                                    <div v-if="!conversations.data.length" class="flex flex-col items-center justify-center w-full h-full p-8 text-gray-500">
                                        <icon name="chat" class="w-16 h-16 mb-4 text-gray-300" />
                                        <p class="mb-4 text-center">{{ __('No conversation found.') }}</p>
                                        <!-- <button @click="showNewChatModal = true" class="px-6 py-2 text-white bg-blue-500 rounded hover:bg-blue-600">
                                            Start New Chat
                                        </button> -->
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Chat Area -->
                <div class="flex-1 h-full bg-white chat-area lg:flex lg:flex-col" :class="!showSidebar ? 'flex flex-col' : 'hidden lg:flex lg:flex-col'">
                    <!-- Chat Header -->
                    <div class="flex-3" v-if="chat">
                        <div class="flex justify-between pb-3 border-b chat-header">
                            <div class="flex items-center">
                                <!-- Mobile Back Button -->
                                <button @click="showSidebar = true" class="mr-3 text-gray-600 lg:hidden hover:text-gray-800">
                                    <svg xmlns="http://www.w3.org/2000/svg" class="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
                                    </svg>
                                </button>
                                <div class="relative w-12 h-12 mr-3">
                                    <img v-if="chat.group_icon" :src="chat.group_icon" class="object-cover w-12 h-12 rounded-full" />
                                    <span v-else class="flex items-center justify-center w-12 h-12 mx-auto rounded-full user_icon">
                                        {{ getInitial(chat.group_name || 'Chat') }}
                                    </span>
                                </div>
                                <div class="flex flex-col pt-1">
                                    <div class="font-semibold name">{{ chat.group_name || 'Direct Chat' }}</div>
                                    <div class="pt-1 text-sm text-gray-600 status digits">
                                        <span v-if="chat.is_group">{{ chat.participants.length }} members</span>
                                        <span v-else>Direct Message</span>
                                    </div>
                                </div>
                            </div>
                            <div class="flex items-center gap-2">
                                <button v-if="chat.is_group" @click="showParticipantsModal = true" class="text-blue-500 hover:text-blue-700" title="View Members">
                                    <icon name="users" class="w-5 h-5" />
                                </button>
                                <!-- Delete button - Only for creator or users with delete permission -->
                                <button v-if="canDeleteConversation()" @click="destroy(chat.id)" class="text-red-500 hover:text-red-700" title="Delete Conversation">
                                    <icon name="trash" class="w-5 h-5" />
                                </button>
                            </div>
                        </div>
                    </div>

                    <!-- Messages -->
                    <div class="flex-1 p-4 overflow-auto messages" ref="messagesContainer">
                        <div v-if="chat && chat.messages && chat.messages.length">
                            <div v-for="(message, index) in chat.messages" :key="index" class="mb-4 message">
                                <!-- System Message -->
                                <div v-if="message.message_type === 'system'" class="text-center">
                                    <small class="px-3 py-1 text-gray-500 bg-gray-100 rounded-full">{{ message.message }}</small>
                                </div>

                                <!-- User Message -->
                                <div v-else :class="message.user_id === user.id ? 'flex justify-end' : 'flex justify-start'">
                                    <div :class="message.user_id === user.id ? 'flex-row-reverse' : 'flex-row'" class="flex w-full max-w-xs sm:max-w-sm md:max-w-md lg:max-w-lg">
                                        <div class="flex-shrink-0">
                                            <div class="relative w-10 h-10 mx-2">
                                                <img v-if="message.user && message.user.photo_path" :src="message.user.photo_path" class="object-cover w-10 h-10 rounded-full" />
                                                <span v-else class="flex items-center justify-center w-10 h-10 text-sm rounded-full user_icon">
                                                    {{ getInitial(getSenderName(message)) }}
                                                </span>
                                            </div>
                                        </div>
                                        <div class="flex-1 min-w-0">
                                            <div :class="message.user_id === user.id ? 'bg-blue-600 text-white' : 'bg-gray-300 text-gray-700'" 
                                                 class="p-3 break-words rounded-lg">
                                                <div class="mb-1 text-xs opacity-75">{{ getSenderName(message) }}</div>
                                                <div class="break-words whitespace-pre-wrap">{{ message.message }}</div>
                                                
                                                <!-- Attachments -->
                                                <div v-if="message.attachments && message.attachments.length" class="mt-2 space-y-2">
                                                    <div v-for="attachment in message.attachments" :key="attachment.id">
                                                        <img v-if="isImage(attachment)" :src="attachment.path" class="object-cover w-full rounded cursor-pointer max-h-96 sm:max-w-sm" style="max-height: 400px;" @click="openImage(attachment.path)" />
                                                        <a v-else :href="attachment.path" target="_blank" class="flex items-center gap-2 text-sm underline break-all">
                                                            <icon name="download" class="flex-shrink-0 w-4 h-4" />
                                                            <span class="break-all">{{ attachment.name }}</span>
                                                        </a>
                                                    </div>
                                                </div>
                                            </div>
                                            <div class="flex items-center gap-2 px-2 mt-1" :class="message.user_id === user.id ? 'text-right justify-end' : 'text-left justify-start'">
                                                <small class="text-xs text-gray-500">{{ formatTime(message.created_at) }}</small>
                                                
                                                <!-- Delete button (only for own messages within 1 minute) -->
                                                <button v-if="canDeleteMessage(message)" 
                                                        @click="deleteMessage(message)" 
                                                        class="text-xs text-red-500 hover:text-red-700"
                                                        title="Delete message">
                                                    <icon name="trash" class="w-4 h-4" />
                                                </button>
                                                
                                                <!-- Read receipts for own messages -->
                                                <div v-if="message.user_id === user.id" class="flex items-center gap-1">
                                                    <!-- Single Gray Tick: Not read by anyone -->
                                                    <svg v-if="!message.reads || message.reads.length === 0" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                        <path d="M14 5L6 13L2 9" stroke="#9ca3af" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                                                    </svg>
                                                    
                                                    <!-- Double Blue Tick: All read (in groups) OR read in direct chat -->
                                                    <svg v-else-if="isAllRead(message)" @click="chat.is_group ? showReadReceipts(message) : null" :class="chat.is_group ? 'cursor-pointer' : ''" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                        <path d="M14 5L6 13L2 9" stroke="#0ea5e9" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                                                        <path d="M11 5L8 8" stroke="#0ea5e9" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                                                    </svg>
                                                    
                                                    <!-- Double Gray Tick: Some read but not all (groups only) -->
                                                    <svg v-else @click="showReadReceipts(message)" class="cursor-pointer" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                        <path d="M14 5L6 13L2 9" stroke="#9ca3af" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                                                        <path d="M11 5L8 8" stroke="#9ca3af" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                                                    </svg>
                                                    
                                                    <!-- Read count for groups (only when some have read) -->
                                                    <small v-if="chat.is_group && message.reads && message.reads.length > 0 && !isAllRead(message)" class="text-xs text-gray-500">
                                                        {{ message.reads.length }}
                                                    </small>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        <div v-else-if="chat && (!chat.messages || !chat.messages.length)" class="flex items-center justify-center w-full h-full text-gray-500">
                            {{ __('No messages yet. Start the conversation!') }}
                        </div>
                        
                        <div v-else class="flex flex-col items-center justify-center w-full h-full p-4 text-center text-gray-500">
                            <icon name="chat" class="w-16 h-16 mb-4 text-gray-300 lg:w-20 lg:h-20" />
                            <p class="text-sm lg:text-base">{{ __('Select a conversation from left to see messages.') }}</p>
                            <button @click="showSidebar = true" class="px-6 py-2 mt-4 text-white bg-blue-500 rounded lg:hidden hover:bg-blue-600">
                                View Conversations
                            </button>
                        </div>
                    </div>

                    <!-- Message Input -->
                    <div class="px-4 pt-4 pb-3 border-t flex-2" v-if="chat && user_access.chat.create">
                        <div class="flex bg-white border rounded-lg shadow write">
                            <div class="flex-1">
                                <textarea v-model="message" 
                                         name="message" 
                                         @keydown.enter.exact.prevent="sendMessage" 
                                         class="block w-full px-4 py-4 text-sm bg-transparent outline-none resize-none" 
                                         rows="2" 
                                         :placeholder="__('Type a message...')" 
                                         autofocus></textarea>
                            </div>
                            <div class="flex items-center gap-2 p-2">
                                <label class="flex items-center justify-center w-10 h-10 text-gray-600 transition-colors bg-gray-100 rounded-full cursor-pointer hover:bg-gray-200 hover:text-gray-800">
                                    <input type="file" @change="handleFileSelect" multiple accept="image/*,.pdf,.doc,.docx" class="hidden" ref="fileInput" />
                                    <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                                    </svg>
                                </label>
                                <button @click="sendMessage" :disabled="isSending" class="flex items-center justify-center w-10 h-10 text-white transition-colors rounded-full" :class="isSending ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-500 hover:bg-blue-600'">
                                    <icon v-if="!isSending" class="w-5 h-5 fill-white" name="send"/>
                                    <svg v-else class="w-5 h-5 text-white animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                </button>
                            </div>
                        </div>
                        <div v-if="selectedFiles.length" class="flex flex-wrap gap-2 mt-2">
                            <div v-for="(file, index) in selectedFiles" :key="index" class="relative">
                                <!-- Image preview for image files -->
                                <div v-if="file.type.startsWith('image/')" class="relative group">
                                    <img :src="getFilePreview(file)" class="object-cover w-20 h-20 border border-gray-300 rounded" />
                                    <button @click="removeFile(index)" class="absolute flex items-center justify-center w-6 h-6 text-white bg-red-500 rounded-full -top-2 -right-2 hover:bg-red-600">
                                        <icon name="x" class="w-4 h-4" />
                                    </button>
                                    <div class="absolute inset-0 flex items-center justify-center text-xs text-white transition-opacity bg-black bg-opacity-50 rounded opacity-0 group-hover:opacity-100">
                                        {{ (file.size / (1024 * 1024)).toFixed(2) }}MB
                                    </div>
                                </div>
                                <!-- File name for non-image files -->
                                <div v-else class="flex items-center gap-2 px-3 py-2 bg-gray-100 border border-gray-300 rounded">
                                    <icon name="paperclip" class="w-4 h-4 text-gray-600" />
                                    <div class="flex flex-col">
                                        <span class="text-sm font-medium">{{ file.name }}</span>
                                        <span class="text-xs text-gray-500">{{ (file.size / (1024 * 1024)).toFixed(2) }}MB</span>
                                    </div>
                                    <button @click="removeFile(index)" class="ml-2 text-red-500 hover:text-red-700">
                                        <icon name="x" class="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    <div v-else-if="chat && !user_access.chat.create" class="p-4 text-center text-gray-600 bg-gray-100 flex-2">
                        <icon name="lock" class="inline-block w-6 h-6 mr-2" />
                        You don't have permission to send messages in this conversation.
                    </div>
                </div>
            </div>
        </div>

        <!-- New Chat Modal -->
        <div v-if="showNewChatModal" class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50" @click.self="showNewChatModal = false">
            <div class="p-6 overflow-y-auto bg-white rounded-lg w-96 max-h-96">
                <h3 class="mb-4 text-lg font-semibold">Start New Chat</h3>
                <div class="space-y-2">
                    <input v-model="userSearch" type="text" placeholder="Search users..." class="w-full px-3 py-2 mb-3 border rounded" />
                    <div v-for="usr in filteredUsers" :key="usr.id" @click="createDirectChat(usr.id)" class="flex items-center gap-3 p-3 rounded cursor-pointer hover:bg-gray-100">
                        <img v-if="usr.photo_path" :src="usr.photo_path" class="w-10 h-10 rounded-full" />
                        <span v-else class="flex items-center justify-center w-10 h-10 rounded-full user_icon">{{ getInitial(usr.first_name) }}</span>
                        <div>
                            <div class="font-medium">{{ usr.first_name }} {{ usr.last_name }}</div>
                            <div class="text-sm text-gray-600">{{ usr.email }}</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- New Group Modal -->
        <div v-if="showNewGroupModal" class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50" @click.self="showNewGroupModal = false">
            <div class="bg-white rounded-lg p-6 w-96 max-h-[80vh] overflow-y-auto">
                <h3 class="mb-4 text-lg font-semibold">Create New Group</h3>
                <input v-model="newGroupName" type="text" placeholder="Group name..." class="w-full px-3 py-2 mb-4 border rounded" />
                
                <!-- Group Icon Upload -->
                <div class="mb-4">
                    <label class="block mb-2 text-sm font-medium text-gray-700">Group Icon (Optional)</label>
                    <input type="file" @change="handleGroupIconSelect" accept="image/*" ref="groupIconInput" class="w-full text-sm" />
                    <div v-if="groupIconPreview" class="mt-2">
                        <img :src="groupIconPreview" class="object-cover w-20 h-20 rounded-full" />
                    </div>
                </div>
                
                <!-- Class Selection -->
                <div v-if="allClasses && allClasses.length" class="pb-4 mb-4 border-b">
                    <h4 class="mb-2 text-sm font-medium text-gray-700">Select Classes (Auto-add all students)</h4>
                    <div class="space-y-2 overflow-y-auto max-h-32">
                        <div v-for="cls in allClasses" :key="cls.id" class="flex items-center gap-2 p-2 rounded hover:bg-blue-50">
                            <input type="checkbox" :value="cls.id" v-model="selectedClassIds" :id="'class-' + cls.id" class="text-blue-600" />
                            <label :for="'class-' + cls.id" class="flex-1 cursor-pointer">{{ cls.name }}</label>
                        </div>
                    </div>
                    <div v-if="selectedClassIds.length" class="mt-2 text-xs text-blue-600">
                        {{ selectedClassIds.length }} {{ selectedClassIds.length === 1 ? 'class' : 'classes' }} selected
                    </div>
                </div>
                
                <!-- Individual User Selection -->
                <div class="mb-3">
                    <h4 class="mb-2 text-sm font-medium text-gray-700">Or Select Individual Users</h4>
                    <div class="overflow-y-auto max-h-48">
                        <div v-for="usr in allUsers" :key="usr.id" class="flex items-center gap-2 p-2 rounded hover:bg-gray-100">
                            <input type="checkbox" :value="usr.id" v-model="selectedUserIds" :id="'user-' + usr.id" />
                            <label :for="'user-' + usr.id" class="flex-1 cursor-pointer">{{ usr.first_name }} {{ usr.last_name }}</label>
                        </div>
                    </div>
                </div>
                
                <button @click="createGroup" :disabled="!newGroupName || (selectedUserIds.length === 0 && selectedClassIds.length === 0)" class="w-full px-4 py-2 text-white bg-green-500 rounded hover:bg-green-600 disabled:opacity-50">
                    Create Group
                </button>
            </div>
        </div>

        <!-- Participants Modal -->
        <div v-if="showParticipantsModal && chat && chat.is_group" class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50" @click.self="showParticipantsModal = false">
            <div class="bg-white rounded-lg p-6 w-96 max-h-[80vh] overflow-y-auto">
                <div class="flex items-center justify-between mb-4">
                    <h3 class="text-lg font-semibold">Group Members</h3>
                    <button @click="showParticipantsModal = false" class="text-gray-500 hover:text-gray-700">
                        <icon name="x" class="w-5 h-5" />
                    </button>
                </div>
                
                <!-- Admin Actions -->
                <div v-if="chat.is_admin" class="flex gap-2 mb-4">
                    <button @click="showEditGroupModal = true; showParticipantsModal = false" class="flex-1 px-3 py-2 text-sm text-white bg-blue-500 rounded hover:bg-blue-600">
                        <icon name="edit" class="inline-block w-4 h-4 mr-1" /> Edit Group
                    </button>
                    <button @click="showAddMembersModal = true; showParticipantsModal = false" class="flex-1 px-3 py-2 text-sm text-white bg-green-500 rounded hover:bg-green-600">
                        <icon name="plus" class="inline-block w-4 h-4 mr-1" /> Add Members
                    </button>
                </div>
                
                <div class="mb-4 text-sm text-gray-600">
                    {{ chat.participants.length }} {{ chat.participants.length === 1 ? 'member' : 'members' }} in this group
                </div>
                
                <div class="space-y-3">
                    <div v-for="participant in chat.participants" :key="participant.id" class="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50" :class="{'opacity-50': !participant.is_active}">
                        <div class="flex-shrink-0">
                            <img v-if="participant.user && participant.user.photo_path" :src="participant.user.photo_path" class="object-cover w-10 h-10 rounded-full" />
                            <span v-else class="flex items-center justify-center w-10 h-10 text-sm font-semibold text-white rounded-full user_icon">
                                {{ getInitial(participant.display_name) }}
                            </span>
                        </div>
                        <div class="flex-1">
                            <div class="font-medium">
                                {{ participant.display_name }}
                                <span v-if="!participant.is_active" class="ml-2 px-2 py-0.5 text-xs text-red-800 bg-red-100 rounded">Inactive</span>
                            </div>
                            <div class="text-xs text-gray-500">
                                <span v-if="chat.created_by === participant.user_id" class="font-medium text-blue-600">Creator</span>
                                <span v-else-if="participant.is_admin" class="font-medium text-green-600">Admin</span>
                                <span v-else>Member</span>
                            </div>
                        </div>
                        <div v-if="participant.is_admin" class="flex-shrink-0">
                            <span class="px-2 py-1 text-xs text-green-800 bg-green-100 rounded">Admin</span>
                        </div>
                        <div v-if="chat.created_by === participant.user_id" class="flex-shrink-0">
                            <span class="px-2 py-1 text-xs text-blue-800 bg-blue-100 rounded">Creator</span>
                        </div>
                        <!-- Toggle Active/Inactive Status -->
                        <button v-if="chat.is_admin && participant.user_id !== user.id && chat.created_by !== participant.user_id" 
                                @click="toggleMemberStatus(participant)" 
                                class="flex-shrink-0 px-2 py-1 text-xs transition-colors rounded"
                                :class="participant.is_active ? 'text-orange-600 bg-orange-50 hover:bg-orange-100' : 'text-green-600 bg-green-50 hover:bg-green-100'"
                                :title="participant.is_active ? 'Deactivate member' : 'Activate member'">
                            {{ participant.is_active ? 'Deactivate' : 'Activate' }}
                        </button>
                        <!-- Remove Button -->
                        <button v-if="chat.is_admin && participant.user_id !== user.id && chat.created_by !== participant.user_id" 
                                @click="removeMember(participant.id)" 
                                class="flex-shrink-0 text-red-500 hover:text-red-700" 
                                title="Remove member">
                            <icon name="x" class="w-4 h-4" />
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Edit Group Modal -->
        <div v-if="showEditGroupModal && chat" class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50" @click.self="showEditGroupModal = false">
            <div class="p-6 bg-white rounded-lg w-96">
                <h3 class="mb-4 text-lg font-semibold">Edit Group</h3>
                <div class="mb-4">
                    <label class="block mb-2 text-sm font-medium text-gray-700">Group Name</label>
                    <input v-model="editGroupName" type="text" placeholder="Group name..." class="w-full px-3 py-2 border rounded" />
                </div>
                <div class="mb-4">
                    <label class="block mb-2 text-sm font-medium text-gray-700">Group Icon</label>
                    <div v-if="chat.group_icon" class="mb-2">
                        <img :src="chat.group_icon" class="object-cover w-20 h-20 rounded-full" />
                    </div>
                    <input type="file" @change="handleEditGroupIconSelect" accept="image/*" ref="editGroupIconInput" class="w-full text-sm" />
                    <div v-if="editGroupIconPreview" class="mt-2">
                        <img :src="editGroupIconPreview" class="object-cover w-20 h-20 rounded-full" />
                    </div>
                </div>
                <div class="flex gap-2">
                    <button @click="showEditGroupModal = false" class="flex-1 px-4 py-2 text-gray-800 bg-gray-300 rounded hover:bg-gray-400">
                        Cancel
                    </button>
                    <button @click="updateGroup" class="flex-1 px-4 py-2 text-white bg-blue-500 rounded hover:bg-blue-600">
                        Update
                    </button>
                </div>
            </div>
        </div>

        <!-- Read Receipts Modal -->
        <div v-if="showReadReceiptsModal && selectedMessageForReads" class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50" @click.self="showReadReceiptsModal = false">
            <div class="bg-white rounded-lg shadow-xl p-6 w-full max-w-md max-h-[80vh] overflow-y-auto">
                <div class="flex items-center justify-between mb-4">
                    <h3 class="text-lg font-semibold">Message Info</h3>
                    <button @click="showReadReceiptsModal = false" class="text-gray-500 hover:text-gray-700">
                        <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                <!-- Read By Section -->
                <div v-if="getReadByUsers.length > 0" class="mb-6">
                    <div class="flex items-center gap-2 mb-2 text-xs font-semibold text-gray-500 uppercase">
                        <svg class="w-4 h-4" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M14 5L6 13L2 9" stroke="#0ea5e9" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                            <path d="M11 5L8 8" stroke="#0ea5e9" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                        Read By ({{ getReadByUsers.length }})
                    </div>
                    <div class="space-y-2">
                        <div v-for="read in getReadByUsers" :key="read.user_id" class="flex items-center gap-3 p-2 rounded hover:bg-gray-50">
                            <div class="relative w-10 h-10">
                                <img v-if="read.user.photo_path" :src="read.user.photo_path" class="object-cover w-10 h-10 rounded-full" />
                                <span v-else class="flex items-center justify-center w-10 h-10 text-sm rounded-full user_icon">
                                    {{ getInitial(read.user.first_name + ' ' + read.user.last_name) }}
                                </span>
                            </div>
                            <div class="flex-1">
                                <div class="text-sm font-medium">{{ read.user.first_name }} {{ read.user.last_name }}</div>
                                <div class="text-xs text-gray-500">{{ formatDateTime(read.read_at) }}</div>
                            </div>
                            <svg class="w-5 h-5" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M14 5L6 13L2 9" stroke="#0ea5e9" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                                <path d="M11 5L8 8" stroke="#0ea5e9" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                            </svg>
                        </div>
                    </div>
                </div>

                <!-- Not Read Section -->
                <div v-if="getNotReadByUsers.length > 0" class="mb-2">
                    <div class="flex items-center gap-2 mb-2 text-xs font-semibold text-gray-500 uppercase">
                        <svg class="w-4 h-4" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M14 5L6 13L2 9" stroke="#9ca3af" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                        Not Read ({{ getNotReadByUsers.length }})
                    </div>
                    <div class="space-y-2">
                        <div v-for="participant in getNotReadByUsers" :key="participant.user_id" class="flex items-center gap-3 p-2 rounded hover:bg-gray-50">
                            <div class="relative w-10 h-10">
                                <img v-if="participant.user && participant.user.photo_path" :src="participant.user.photo_path" class="object-cover w-10 h-10 rounded-full" />
                                <span v-else class="flex items-center justify-center w-10 h-10 text-sm rounded-full user_icon">
                                    {{ getInitial(participant.display_name) }}
                                </span>
                            </div>
                            <div class="flex-1">
                                <div class="text-sm font-medium">{{ participant.display_name }}</div>
                                <div class="text-xs text-gray-400">Not read yet</div>
                            </div>
                            <svg class="w-5 h-5" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M14 5L6 13L2 9" stroke="#d1d5db" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                            </svg>
                        </div>
                    </div>
                </div>

                <!-- If no one in group (shouldn't happen but just in case) -->
                <div v-if="getReadByUsers.length === 0 && getNotReadByUsers.length === 0" class="py-8 text-center text-gray-500">
                    No participants
                </div>
            </div>
        </div>

        <!-- Add Members Modal -->
        <div v-if="showAddMembersModal && chat" class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50" @click.self="showAddMembersModal = false">
            <div class="bg-white rounded-lg p-6 w-96 max-h-[80vh] overflow-y-auto">
                <h3 class="mb-4 text-lg font-semibold">Add Members to {{ chat.group_name }}</h3>
                <div class="mb-4">
                    <input v-model="addMemberSearch" type="text" placeholder="Search users..." class="w-full px-3 py-2 mb-3 border rounded" />
                    
                    <!-- Filter Tabs -->
                    <div class="flex gap-2 mb-3">
                        <button @click="memberFilterType = 'all'" 
                                :class="memberFilterType === 'all' ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-700'"
                                class="flex-1 px-3 py-1 text-sm rounded hover:opacity-90">
                            All Users
                        </button>
                        <button @click="memberFilterType = 'staff'" 
                                :class="memberFilterType === 'staff' ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-700'"
                                class="flex-1 px-3 py-1 text-sm rounded hover:opacity-90">
                            Staff Only
                        </button>
                    </div>
                    
                    <div class="space-y-2 overflow-y-auto max-h-64">
                        <div v-for="usr in availableUsersToAdd" :key="usr.id" class="flex items-center gap-2 p-2 rounded hover:bg-gray-100">
                            <input type="checkbox" :value="usr.id" v-model="newMemberIds" :id="'add-user-' + usr.id" />
                            <label :for="'add-user-' + usr.id" class="flex items-center flex-1 gap-2 cursor-pointer">
                                <img v-if="usr.photo_path" :src="usr.photo_path" class="w-8 h-8 rounded-full" />
                                <span v-else class="flex items-center justify-center w-8 h-8 text-xs text-white rounded-full user_icon">{{ getInitial(usr.first_name) }}</span>
                                <div class="flex-1">
                                    <div>{{ usr.first_name }} {{ usr.last_name }}</div>
                                    <div v-if="usr.role" class="text-xs text-gray-500">{{ usr.role.name }}</div>
                                </div>
                            </label>
                            <label class="flex items-center gap-1 text-xs text-gray-600 cursor-pointer" :for="'admin-' + usr.id">
                                <input type="checkbox" :value="usr.id" v-model="newAdminIds" :id="'admin-' + usr.id" class="w-4 h-4" />
                                <span>Admin</span>
                            </label>
                        </div>
                    </div>
                </div>
                <div class="flex gap-2">
                    <button @click="showAddMembersModal = false" class="flex-1 px-4 py-2 text-gray-800 bg-gray-300 rounded hover:bg-gray-400">
                        Cancel
                    </button>
                    <button @click="addMembers" :disabled="newMemberIds.length === 0" class="flex-1 px-4 py-2 text-white bg-green-500 rounded hover:bg-green-600 disabled:opacity-50">
                        Add {{ newMemberIds.length }} {{ newMemberIds.length === 1 ? 'Member' : 'Members' }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Icon from '@/Shared/Icon'
import pickBy from 'lodash/pickBy'
import Layout from '@/Shared/Layout'
import throttle from 'lodash/throttle'
import mapValues from 'lodash/mapValues'
import Pagination from '@/Shared/Pagination'
import SearchInput from '@/Shared/SearchInput'
import { Link, Head } from '@inertiajs/inertia-vue3'
import moment from 'moment'

export default {
    metaInfo: { title: 'Chat' },
    components: {
        SearchInput,
        Icon, Pagination, Link, Head
    },
    layout: Layout,
    props: {
        filters: Object,
        conversations: Object,
        chat: Object,
        title: String,
        allUsers: {
            type: Array,
            default: () => []
        },
        allClasses: {
            type: Array,
            default: () => []
        }
    },
    remember: 'form',
    data() {
        return {
            message: null,
            user: this.$page.props.auth.user,
            user_access: this.$page.props.auth.user.access,
            form: {
                search: this.filters.search,
            },
            showNewChatModal: false,
            showNewGroupModal: false,
            showParticipantsModal: false,
            showEditGroupModal: false,
            showAddMembersModal: false,
            showReadReceiptsModal: false,
            selectedMessageForReads: null,
            userSearch: '',
            newGroupName: '',
            editGroupName: '',
            editGroupIconPreview: null,
            editGroupIconFile: null,
            groupIconPreview: null,
            groupIconFile: null,
            addMemberSearch: '',
            memberFilterType: 'all', // 'all' or 'staff'
            newMemberIds: [],
            newAdminIds: [],
            selectedUserIds: [],
            selectedClassIds: [],
            selectedFiles: [],
            isSending: false,
            showSidebar: !this.chat, // Show sidebar if no chat selected
        }
    },
    computed: {
        filteredUsers() {
            if (!this.allUsers) return [];
            const search = this.userSearch.toLowerCase();
            return this.allUsers.filter(u => 
                u.id !== this.user.id &&
                (u.first_name.toLowerCase().includes(search) || 
                 u.last_name.toLowerCase().includes(search) ||
                 u.email.toLowerCase().includes(search))
            );
        },
        availableUsersToAdd() {
            if (!this.allUsers || !this.chat) return [];
            const existingUserIds = this.chat.participants.map(p => p.user_id);
            const search = this.addMemberSearch.toLowerCase();
            
            let users = this.allUsers.filter(u => !existingUserIds.includes(u.id));
            
            // Filter by staff if selected
            if (this.memberFilterType === 'staff') {
                users = users.filter(u => {
                    // Staff are users who don't have student role/category/class
                    const isNotStudent = (!u.role || (u.role.slug !== 'student' && u.role.name.toLowerCase() !== 'student')) 
                        && !u.student_category_id 
                        && !u.class_id;
                    return isNotStudent;
                });
            }
            
            // Apply search filter
            if (search) {
                users = users.filter(u => 
                    (u.first_name.toLowerCase().includes(search) || 
                     u.last_name.toLowerCase().includes(search) ||
                     u.email.toLowerCase().includes(search))
                );
            }
            
            return users;
        },
        getReadByUsers() {
            if (!this.selectedMessageForReads || !this.selectedMessageForReads.reads) return [];
            return this.selectedMessageForReads.reads;
        },
        getNotReadByUsers() {
            if (!this.selectedMessageForReads || !this.chat || !this.chat.participants) return [];
            
            // Get IDs of users who read the message
            const readUserIds = this.selectedMessageForReads.reads 
                ? this.selectedMessageForReads.reads.map(r => r.user_id) 
                : [];
            
            // Filter participants: exclude sender and those who already read
            return this.chat.participants.filter(p => 
                p.user_id !== this.selectedMessageForReads.user_id && // Exclude sender
                !readUserIds.includes(p.user_id) // Exclude those who read
            );
        }
    },
    watch: {
        form: {
            deep: true,
            handler: throttle(function() {
                this.$inertia.get(this.route('chat'), pickBy(this.form), { preserveState: true })
            }, 150),
        },
        chat: {
            immediate: true,
            handler(newChat, oldChat) {
                // Cleanup old channel
                if (oldChat && oldChat.id) {
                    window.Echo.leave(`chat.${oldChat.id}`);
                }
                
                // Setup new channel
                if (newChat && newChat.id) {
                    this.$nextTick(() => {
                        this.setupPusher();
                    });
                }
            }
        }
    },
    methods: {
        reset() {
            this.form = mapValues(this.form, () => null)
        },
        
        handleConversationClick() {
            // Hide sidebar on mobile when conversation is clicked
            if (window.innerWidth < 1024) { // lg breakpoint
                this.showSidebar = false;
            }
        },
        
        getInitial(name) {
            if (!name) return '?';
            return name.charAt(0).toUpperCase();
        },
        
        getSenderName(message) {
            if (message.user) {
                return message.user.first_name + ' ' + message.user.last_name;
            }
            if (message.contact) {
                return message.contact.first_name + ' ' + message.contact.last_name;
            }
            return 'Unknown';
        },
        
        formatDate(date) {
            return moment(date).format('DD MMM');
        },
        
        formatTime(date) {
            return moment(date).fromNow();
        },
        
        formatDateTime(date) {
            return moment(date).format('DD MMM, h:mm A');
        },
        
        showReadReceipts(message) {
            this.selectedMessageForReads = message;
            this.showReadReceiptsModal = true;
        },
        
        isAllRead(message) {
            if (!message.reads || message.reads.length === 0) {
                return false;
            }
            
            // For direct chat: if anyone read it, it's fully read (blue tick)
            if (!this.chat.is_group) {
                return message.reads.length > 0;
            }
            
            // For groups: check if all participants (except sender) have read
            if (!this.chat.participants) {
                return false;
            }
            
            // Count participants excluding the sender
            const participantsExcludingSender = this.chat.participants.filter(p => p.user_id !== message.user_id);
            const totalRecipients = participantsExcludingSender.length;
            
            // All recipients have read
            return message.reads.length >= totalRecipients;
        },
        
        isImage(attachment) {
            return attachment.mime_type && attachment.mime_type.startsWith('image/');
        },
        
        openImage(url) {
            window.open(url, '_blank');
        },
        
        getFilePreview(file) {
            return URL.createObjectURL(file);
        },
        
        handleFileSelect(event) {
            const files = Array.from(event.target.files);
            const maxSize = 10 * 1024 * 1024; // 10MB in bytes
            const validFiles = [];
            const errors = [];
            
            files.forEach(file => {
                if (file.size > maxSize) {
                    errors.push(`${file.name} is too large (${(file.size / (1024 * 1024)).toFixed(2)}MB). Max size is 10MB.`);
                } else {
                    validFiles.push(file);
                }
            });
            
            if (errors.length > 0) {
                alert('Some files were not added:\n\n' + errors.join('\n'));
            }
            
            this.selectedFiles = [...this.selectedFiles, ...validFiles];
            
            // Reset input
            if (this.$refs.fileInput) {
                this.$refs.fileInput.value = '';
            }
        },
        
        removeFile(index) {
            this.selectedFiles.splice(index, 1);
        },
        
        async sendMessage() {
            if (!this.message && this.selectedFiles.length === 0) return;
            if (this.isSending) return; // Prevent double-sending
            
            const formData = new FormData();
            formData.append('conversation_id', this.chat.id);
            formData.append('message', this.message || '');
            
            this.selectedFiles.forEach((file, index) => {
                formData.append(`attachments[${index}]`, file);
            });
            
            const messageText = this.message;
            const tempFiles = [...this.selectedFiles];
            
            // Clear input immediately
            this.message = '';
            this.selectedFiles = [];
            this.isSending = true; // Set loading state
            
            try {
                const response = await this.axios.post(this.route('chat.message'), formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                });
                
                if (response.data.success && response.data.message) {
                    // Add message immediately to show it
                    const exists = this.chat.messages.some(m => m.id === response.data.message.id);
                    if (!exists) {
                        this.chat.messages.push(response.data.message);
                    }
                    
                    this.$nextTick(() => {
                        this.scrollToBottom();
                    });
                } else {
                    // Restore message on failure
                    this.message = messageText;
                    this.selectedFiles = tempFiles;
                    alert('Failed to send message: ' + (response.data.error || 'Unknown error'));
                }
            } catch (error) {
                // Restore message on error
                this.message = messageText;
                this.selectedFiles = tempFiles;
                console.error('Send message error:', error);
                console.error('Error response:', error.response);
                alert('Failed to send message: ' + (error.response?.data?.message || error.message));
            } finally {
                this.isSending = false; // Reset loading state
            }
        },
        
        async createDirectChat(userId) {
            try {
                await this.axios.post(this.route('chat.direct.create'), {
                    user_id: userId,
                    _token: this.$page.props.csrf_token
                });
                this.showNewChatModal = false;
                this.$inertia.reload();
            } catch (error) {
                console.error('Create chat error:', error);
                alert('Failed to create chat. Please try again.');
            }
        },
        
        async createGroup() {
            if (!this.newGroupName || (this.selectedUserIds.length === 0 && this.selectedClassIds.length === 0)) return;
            
            try {
                const formData = new FormData();
                formData.append('group_name', this.newGroupName);
                
                // Append arrays properly
                if (this.selectedUserIds.length > 0) {
                    this.selectedUserIds.forEach(id => formData.append('user_ids[]', id));
                }
                
                if (this.selectedClassIds.length > 0) {
                    this.selectedClassIds.forEach(id => formData.append('class_ids[]', id));
                }
                
                // Append file if exists
                if (this.groupIconFile) {
                    formData.append('group_icon', this.groupIconFile);
                }
                
                const response = await this.axios.post(this.route('chat.group.create'), formData);
                
                this.showNewGroupModal = false;
                this.newGroupName = '';
                this.selectedUserIds = [];
                this.selectedClassIds = [];
                this.groupIconFile = null;
                this.groupIconPreview = null;
                
                if (this.$refs.groupIconInput) {
                    this.$refs.groupIconInput.value = '';
                }
                
                if (response.data.success && response.data.conversation) {
                    this.$inertia.visit(this.route('chat.show', response.data.conversation.id));
                }
            } catch (error) {
                console.error('Create group error:', error);
                console.error('Error details:', {
                    message: error.message,
                    response: error.response?.data,
                    status: error.response?.status
                });
                alert('Failed to create group: ' + (error.response?.data?.message || error.response?.data?.error || error.message));
            }
        },
        
        handleGroupIconSelect(event) {
            const file = event.target.files[0];
            if (file) {
                this.groupIconFile = file;
                const reader = new FileReader();
                reader.onload = (e) => {
                    this.groupIconPreview = e.target.result;
                };
                reader.readAsDataURL(file);
            }
        },
        
        handleEditGroupIconSelect(event) {
            const file = event.target.files[0];
            if (file) {
                this.editGroupIconFile = file;
                const reader = new FileReader();
                reader.onload = (e) => {
                    this.editGroupIconPreview = e.target.result;
                };
                reader.readAsDataURL(file);
            }
        },
        
        async updateGroup() {
            if (!this.editGroupName) return;
            
            try {
                const formData = new FormData();
                formData.append('group_name', this.editGroupName);
                formData.append('_method', 'PUT');
                
                if (this.editGroupIconFile) {
                    formData.append('group_icon', this.editGroupIconFile);
                }
                
                const response = await this.axios.post(this.route('chat.update', this.chat.id), formData);
                
                if (response.data.success) {
                    this.showEditGroupModal = false;
                    this.editGroupIconFile = null;
                    this.editGroupIconPreview = null;
                    
                    if (this.$refs.editGroupIconInput) {
                        this.$refs.editGroupIconInput.value = '';
                    }
                    
                    this.$inertia.reload();
                }
            } catch (error) {
                console.error('Update group error:', error);
                console.error('Error details:', {
                    message: error.message,
                    response: error.response?.data,
                    status: error.response?.status
                });
                alert('Failed to update group: ' + (error.response?.data?.message || error.response?.data?.error || error.message));
            }
        },
        
        async addMembers() {
            if (this.newMemberIds.length === 0) return;
            
            try {
                const response = await this.axios.post(this.route('chat.addMembers', this.chat.id), {
                    user_ids: this.newMemberIds,
                    admin_ids: this.newAdminIds, // IDs of users to add as admin
                    _token: this.$page.props.csrf_token
                });
                
                if (response.data.success) {
                    this.showAddMembersModal = false;
                    this.newMemberIds = [];
                    this.newAdminIds = [];
                    this.addMemberSearch = '';
                    this.memberFilterType = 'all';
                    this.$inertia.reload();
                }
            } catch (error) {
                console.error('Add members error:', error);
                alert('Failed to add members. Please try again.');
            }
        },
        
        async toggleMemberStatus(participant) {
            const action = participant.is_active ? 'deactivate' : 'activate';
            if (!confirm(`Are you sure you want to ${action} this member?`)) return;
            
            try {
                const response = await this.axios.patch(
                    this.route('chat.toggleParticipantStatus', { 
                        chat: this.chat.id, 
                        participant: participant.id 
                    }),
                    {
                        _token: this.$page.props.csrf_token
                    }
                );
                
                if (response.data.success) {
                    // Update participant status locally
                    participant.is_active = response.data.is_active;
                    // Reload to reflect changes
                    this.$inertia.reload();
                }
            } catch (error) {
                console.error('Toggle member status error:', error);
                alert('Failed to change member status. Please try again.');
            }
        },
        
        async removeMember(participantId) {
            if (!confirm('Are you sure you want to remove this member from the group?')) return;
            
            try {
                const response = await this.axios.delete(this.route('chat.removeMember', { chat: this.chat.id, participant: participantId }), {
                    data: {
                        _token: this.$page.props.csrf_token
                    }
                });
                
                if (response.data.success) {
                    this.$inertia.reload();
                }
            } catch (error) {
                console.error('Remove member error:', error);
                alert('Failed to remove member. Please try again.');
            }
        },
        
        canDeleteConversation() {
            if (!this.chat) return false;
            
            // User has delete permission from access control
            const hasDeletePermission = this.user_access.chat.delete;
            
            // User is the creator of this conversation
            const isCreator = this.chat.created_by === this.user.id;
            
            return isCreator || hasDeletePermission;
        },
        
        destroy(id) {
            if (confirm('Are you sure you want to delete this conversation?')) {
                this.axios.delete(this.route('chat.destroy', id))
                    .then(response => {
                        if (response.data.success) {
                            this.$inertia.visit(this.route('chat'));
                        } else {
                            alert('Failed to delete: ' + (response.data.error || 'Unknown error'));
                        }
                    })
                    .catch(error => {
                        console.error('Delete error:', error);
                        alert('Error: ' + (error.response?.data?.error || error.message));
                    });
            }
        },
        
        scrollToBottom() {
            if (this.$refs.messagesContainer) {
                this.$refs.messagesContainer.scrollTop = this.$refs.messagesContainer.scrollHeight;
            }
        },
        
        reloadConversations() {
            // Reload conversations to update unread counts
            this.$inertia.reload({ only: ['conversations'], preserveScroll: true });
        },
        
        setupPusher() {
            if (!this.chat || !this.chat.id) return;
            
            const channelName = `chat.${this.chat.id}`;
            
            // Don't leave the channel - just get/create it
            const channel = window.Echo.channel(channelName);
            
            channel.listen('MessageSent', (e) => {
                if (e.message) {
                    const exists = this.chat.messages.some(m => m.id === e.message.id);
                    if (!exists) {
                        this.chat.messages.push(e.message);
                        this.$nextTick(() => {
                            this.scrollToBottom();
                        });
                    }
                }
            });
            
            
            channel.listen('MessageDeleted', (e) => {
                if (e.message_id) {
                    const messageId = parseInt(e.message_id);
                    const index = this.chat.messages.findIndex(m => m.id === messageId);
                    if (index !== -1) {
                        this.chat.messages.splice(index, 1);
                    }
                }
            });
            
            channel.listen('.MessageRead', (e) => {
                if (e.message_id) {
                    const messageId = parseInt(e.message_id);
                    const messageIndex = this.chat.messages.findIndex(m => m.id === messageId);
                    
                    if (messageIndex !== -1) {
                        const message = this.chat.messages[messageIndex];
                        
                        if (!message.reads) {
                            message.reads = [];
                        }
                        
                        const alreadyRead = message.reads.some(r => r.user_id === e.user_id);
                        
                        if (!alreadyRead) {
                            message.reads.push({
                                user_id: e.user_id,
                                read_at: e.read_at,
                                user: {
                                    id: e.user_id,
                                    first_name: e.user_name ? e.user_name.split(' ')[0] : 'User',
                                    last_name: e.user_name ? e.user_name.split(' ').slice(1).join(' ') : '',
                                    photo_path: e.user_photo
                                }
                            });
                            
                            message.read_count = message.reads.length;
                            this.$set(this.chat.messages, messageIndex, { ...message });
                        }
                    }
                } else {
                    this.chat.messages.forEach((message, index) => {
                        if (message.user_id !== e.user_id) {
                            if (!message.reads) {
                                message.reads = [];
                            }
                            
                            const alreadyRead = message.reads.some(r => r.user_id === e.user_id);
                            if (!alreadyRead) {
                                message.reads.push({
                                    user_id: e.user_id,
                                    read_at: e.read_at,
                                    user: {
                                        id: e.user_id,
                                        first_name: e.user_name ? e.user_name.split(' ')[0] : 'User',
                                        last_name: e.user_name ? e.user_name.split(' ').slice(1).join(' ') : '',
                                        photo_path: e.user_photo
                                    }
                                });
                                
                                message.read_count = message.reads.length;
                                this.$set(this.chat.messages, index, { ...message });
                            }
                        }
                    });
                }
                
                const convIndex = this.conversations.data.findIndex(c => c.id === this.chat.id);
                if (convIndex !== -1) {
                    const updatedConv = { ...this.conversations.data[convIndex] };
                    updatedConv.unread_count = Math.max(0, (updatedConv.unread_count || 0) - 1);
                    this.conversations.data.splice(convIndex, 1, updatedConv);
                }
            });
        },
        
        // Check if message can be deleted (within 1 minute)
        canDeleteMessage(message) {
            if (message.user_id !== this.user.id) return false;
            
            const messageTime = new Date(message.created_at).getTime();
            const currentTime = new Date().getTime();
            const oneMinute = 60 * 1000; // 1 minute in milliseconds
            
            return (currentTime - messageTime) < oneMinute;
        },
        
        // Delete own message
        async deleteMessage(message) {
            if (!this.canDeleteMessage(message)) {
                alert('You can only delete messages within 1 minute of sending.');
                return;
            }
            
            if (!confirm('Delete this message?')) return;
            
            try {
                const response = await this.axios.delete(this.route('chat.message.delete', message.id));
                
                if (response.data.success) {
                    // Remove message from list
                    const index = this.chat.messages.findIndex(m => m.id === message.id);
                    if (index !== -1) {
                        this.chat.messages.splice(index, 1);
                    }
                }
            } catch (error) {
                console.error('Delete message error:', error);
                alert('Failed to delete message.');
            }
        },
    },
    
    mounted() {
        this.scrollToBottom();
        
        // Setup Pusher if chat is open
        if (this.chat) {
            this.setupPusher();
            this.editGroupName = this.chat.group_name || '';
        }
        
        // Listen to user's personal channel
        const userChannelName = `user.${this.user.id}`;
        
        window.Echo.channel(userChannelName)
            .listen('.MessageSent', (e) => {
                if (e.message && e.message.conversation_id) {
                    const messageConvId = parseInt(e.message.conversation_id);
                    
                    if (this.chat && this.chat.id === messageConvId) {
                        return;
                    }
                    
                    const convIndex = this.conversations.data.findIndex(c => c.id === messageConvId);
                    
                    if (convIndex !== -1) {
                        const updatedConv = { ...this.conversations.data[convIndex] };
                        updatedConv.unread_count = (updatedConv.unread_count || 0) + 1;
                        updatedConv.last_message = e.message.message;
                        updatedConv.last_message_at = e.message.created_at;
                        this.conversations.data.splice(convIndex, 1, updatedConv);
                    }
                }
            })
            .listen('.MessageRead', (e) => {
                this.reloadConversations();
            });
    },
    
    beforeUnmount() {
        if (this.chat) {
            window.Echo.leave(`chat.${this.chat.id}`);
        }
        window.Echo.leave(`user.${this.user.id}`);
    }
}
</script>

<style scoped>
.user_icon {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    font-weight: bold;
    display: flex;
    align-items: center;
    justify-content: center;
}
</style>
